import { useEffect, useRef, useState } from "react";
import { motion, useInView, useAnimation } from "framer-motion";
import AboutBg from '../../../Assets/Home/AboutBg.png';

const skills = [
  {
    label: "Artificial Intelligent",
    percent: 90,
    icon: (
      // Document/code icon
      <svg viewBox="0 0 24 24" fill="none" className="w-6 h-6 text-blue-500" xmlns="http://www.w3.org/2000/svg">
        <rect x="4" y="2" width="16" height="20" rx="2" stroke="currentColor" strokeWidth="1.7" />
        <path d="M8 7h8M8 11h8M8 15h5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
      </svg>
    ),
  },
  {
    label: "Cloud Migration",
    percent: 80,
    icon: (
      // Two phones / cloud icon
      <svg viewBox="0 0 24 24" fill="none" className="w-6 h-6 text-blue-500" xmlns="http://www.w3.org/2000/svg">
        <rect x="2" y="5" width="8" height="14" rx="2" stroke="currentColor" strokeWidth="1.7" />
        <rect x="14" y="5" width="8" height="14" rx="2" stroke="currentColor" strokeWidth="1.7" />
        <path d="M10 12h4" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
      </svg>
    ),
  },
];

function SkillBar({ label, percent, icon, delay }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  const controls = useAnimation();

  useEffect(() => {
    if (inView) controls.start({ width: `${percent}%` });
  }, [inView, controls, percent]);

  return (
    <div ref={ref} className="mb-7 last:mb-0">
      <div className="flex items-center gap-3 mb-2">
        {/* Icon box */}
        <div className="w-11 h-11 rounded-xl bg-blue-50 flex items-center justify-center shrink-0">
          {icon}
        </div>
        <span className="flex-1 font-bold text-gray-900 text-[1rem]">{label}</span>
        <span className="text-blue-500 font-bold text-[1rem]">{percent} %</span>
      </div>
      {/* Progress track */}
      <div className="h-2.5 bg-gray-200 rounded-full overflow-hidden">
        <motion.div
          className="h-full bg-blue-600 rounded-full"
          initial={{ width: 0 }}
          animate={controls}
          transition={{ duration: 1.1, delay: delay, ease: "easeOut" }}
        />
      </div>
    </div>
  );
}

export default function AboutSection() {
  const leftRef = useRef(null);
  const leftInView = useInView(leftRef, { once: true, margin: "-80px" });

  return (
    <section className="w-full bg-white py-8 lg:py-12 overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 flex flex-col lg:flex-row items-center gap-16">

        {/* ── LEFT: Image composition ── */}
       <div 
  ref={leftRef} 
  style={{ backgroundImage: `url(${AboutBg})` }} 
  className="
    flex-1 
    relative 
    min-h-[420px] sm:min-h-[480px] md:min-h-[540px] lg:min-h-[580px] 
    flex items-center justify-center 
    bg-cover bg-center bg-no-repeat
    px-3 sm:px-4 md:px-6
  "
>
  {/* Background overlay */}
  <div className="absolute inset-0 bg-black/5 md:bg-black/0 rounded-2xl sm:rounded-none" />

  {/* Responsive image container */}
  <motion.div
    className="relative z-20 rounded-xl sm:rounded-2xl overflow-hidden shadow-lg sm:shadow-xl w-full"
    style={{ 
      maxWidth: "min(90%, 470px)",
      aspectRatio: "470/500"
    }}
    initial={{ opacity: 0, x: -40 }}
    animate={leftInView ? { opacity: 1, x: 0 } : {}}
    transition={{ duration: 0.7, delay: 0.25, ease: "easeOut" }}
  >
    <img
      src="https://images.unsplash.com/photo-1519389950473-47ba0277781c?w=700&auto=format&fit=crop&q=80"
      alt="Person working on laptop with dashboard"
      className="w-full h-full object-cover object-center"
      loading="lazy"
    />
    
    {/* Subtle gradient overlay for better contrast */}
    <div className="absolute inset-0 bg-gradient-to-t from-black/5 via-transparent to-transparent pointer-events-none" />
  </motion.div>

  {/* Floating decorative elements - responsive visibility */}
  <div className="absolute top-4 left-4 sm:top-8 sm:left-8 w-8 h-8 sm:w-12 sm:h-12 rounded-full bg-orange-500/30 blur-md pointer-events-none hidden xs:block" />
  <div className="absolute bottom-4 right-4 sm:bottom-8 sm:right-8 w-10 h-10 sm:w-16 sm:h-16 rounded-full bg-blue-500/20 blur-lg pointer-events-none hidden sm:block" />
</div>

        {/* ── RIGHT: Text + Skills ── */}
        <div className="flex-1 max-w-xl">

          {/* Badge */}
          {/* <motion.div
            initial={{ opacity: 0, y: -12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.45 }}
            className="mb-5"
          >
            <span className="inline-block px-5 py-1.5 font-jakarta rounded-full border border-gray-300 bg-gray-50 text-gray-600 text-xs font-semibold tracking-widest uppercase">
              About Us
            </span>
          </motion.div> */}

          {/* Heading */}
       <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.55, delay: 0.1 }}
            className="text-[1.75rem] sm:text-[1.85rem] md:text-[2.25rem] lg:text-[2.85rem] font-jakarta font-bold text-gray-900 leading-tight mb-5"
            >
            Transforming Businesses
            <br />
            Through{" "}
            <span className="text-blue-500">Software</span>{" "}
            Innovation
            </motion.h2>

          {/* Description */}
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.18 }}
            className="text-gray-500 text-sm md:text-base  leading-relaxed mb-4"
          >
            Welcome to KGS Technology Group, a leading provider of innovative IT solutions for various industries. Our team is passionate about harnessing the power of technology to help our clients achieve their goals and succeed in a rapidly evolving digital landscape.
          </motion.p>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.18 }}
            className="text-gray-500 text-sm md:text-base  leading-relaxed mb-10"
          >
            At KGS Technology Group, we believe in the transformative power of IT solutions. Our team is passionate about helping our clients leverage the latest technologies to achieve their goals and stay ahead of the competition. We offer a wide range of IT services, including consulting, software development, system integration, and project management, tailored to meet each client's unique needs.
          </motion.p>

          {/* Skill Bars */}
          {/* <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.26 }}
          >
            {skills.map((skill, i) => (
              <SkillBar
                key={skill.label}
                label={skill.label}
                percent={skill.percent}
                icon={skill.icon}
                delay={i * 0.15}
              />
            ))}
          </motion.div> */}
        </div>
      </div>
    </section>
  );
}